<template>
  <div class="service-search">
    <h1 class="service-search__title">{{ title }}</h1>
    <div
      ref="searchBarRef"
      class="service-search__bar"
      @focusin="handleFocus"
      @pointerdown="handleFocus"
    >
      <a-input-search
        v-model="keyword"
        class="service-search__input"
        :placeholder="placeholder"
        size="large"
        allow-clear
        search-button
        :max-length="50"
        @clear="handleClear"
        @blur="handleBlur"
        @search="handleSearch"
        @press-enter="handleSearch"
      >
        <template #button-default>{{ searchButtonText }}</template>
      </a-input-search>

      <SearchSuggestionDropdown
        v-if="visibleSuggestions"
        class="service-search__suggestions"
        :keyword="keyword"
        :items="suggestions"
        :loading="suggestionsLoading"
        :error="suggestionsError"
        empty-text="暂无数据"
        @select="handleSuggestionSelect"
        @retry="emit('suggestion-retry')"
      />
    </div>
  </div>
</template>

<script setup>
/**
 * @description 服务页面搜索组件（帮助中心/下载中心通用）
 * @param {string} title - 页面标题，默认为"帮助中心"
 * @param {string} placeholder - 搜索框占位符，默认为"请输入关键词查找"
 * @param {string} keyword - 搜索关键词初始值/受控值，与父组件 searchKeyword 同步
 * @emits search - 搜索时触发，传递搜索关键词
 */

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import SearchSuggestionDropdown from '~/components/base/SearchSuggestionDropdown.vue'

const props = defineProps({
  /**
   * @description 页面标题
   * @type {string}
   * @default '帮助中心'
   */
  title: {
    type: String,
    default: '帮助中心',
  },
  /**
   * @description 搜索框占位符
   * @type {string}
   * @default '请输入关键词查找'
   */
  placeholder: {
    type: String,
    default: '请输入关键词查找',
  },
  /**
   * @description 搜索关键词初始值/受控值，与父组件状态同步（如 URL 参数、清空筛选时）
   * @type {string}
   * @default ''
   */
  keyword: {
    type: String,
    default: '',
  },
  searchButtonText: {
    type: String,
    default: '查询',
  },
  enableSuggestions: {
    type: Boolean,
    default: false,
  },
  suggestions: {
    type: Array,
    default: () => [],
  },
  suggestionsLoading: {
    type: Boolean,
    default: false,
  },
  suggestionsError: {
    type: Boolean,
    default: false,
  },
})

const keyword = ref(props.keyword)
const focused = ref(false)
const searchBarRef = ref(null)
const emit = defineEmits([
  'update:keyword',
  'search',
  'focus',
  'suggestion-select',
  'suggestion-retry',
])

watch(
  () => props.keyword,
  (v) => {
    keyword.value = v ?? ''
  }
)

watch(keyword, (value) => {
  emit('update:keyword', value ?? '')
})

const visibleSuggestions = computed(
  () => props.enableSuggestions && focused.value && Boolean(keyword.value.trim())
)

const handleClear = () => {
  keyword.value = ''
  emit('update:keyword', '')
  emit('search', '')
  focused.value = false
}

const handleFocus = () => {
  if (focused.value) return
  focused.value = true
  emit('focus', keyword.value.trim())
}

const handleBlur = () => {
  window.setTimeout(() => {
    if (import.meta.client && searchBarRef.value?.contains(document.activeElement)) {
      return
    }
    focused.value = false
  }, 120)
}

const blurActiveElement = () => {
  if (!import.meta.client) return
  const activeElement = document.activeElement
  if (activeElement && typeof activeElement.blur === 'function') {
    activeElement.blur()
  }
}

const handleSuggestionSelect = (item) => {
  emit('suggestion-select', item)
  focused.value = false
  blurActiveElement()
}

const handleOutsidePointer = (event) => {
  if (!searchBarRef.value?.contains(event.target)) focused.value = false
}

const handlePageScroll = (event) => {
  if (!searchBarRef.value?.contains(event.target)) focused.value = false
}

onMounted(() => {
  document.addEventListener('pointerdown', handleOutsidePointer)
  window.addEventListener('scroll', handlePageScroll, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', handleOutsidePointer)
  window.removeEventListener('scroll', handlePageScroll, true)
})

/**
 * @description 处理搜索
 */
const handleSearch = () => {
  emit('search', keyword.value.trim())
  focused.value = false
  blurActiveElement()
}
</script>

<style lang="scss" scoped>
@use '~/styles/arco-input-mixins' as arco-input;

.service-search {
  max-width: $container-max-width;
  margin: 0 auto;
  text-align: center;
  padding: 32px 0 48px;
  background-color: $bg-color;
  border-bottom: none;
  box-sizing: border-box;

  &__title {
    font-size: $font-size-display-1;
    line-height: $line-height-display-1;
    font-weight: 600;
    color: $text-title-color;
    margin: 0 0 16px 0;
  }

  &__bar {
    position: relative;
    max-width: 560px;
    margin: 0 auto;
    display: flex;
    justify-content: center;
  }

  &__input {
    width: 560px;

    :deep(.arco-input-wrapper) {
      height: 48px;
      background: $bg-white;
      border-color: $border-light-color;
      border-radius: $radius-6 0 0 $radius-6;
      padding: 0 16px;
    }

    :deep(.arco-input-inner-wrapper) {
      height: 48px;
    }

    :deep(.arco-input) {
      font-size: 16px;
      line-height: 24px;
    }

    :deep(.arco-input-search-btn) {
      width: 88px;
      height: 48px;
      border-radius: 0 $radius-6 $radius-6 0;
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
    }

    @include arco-input.show-clear-action-on-focus;

    @include respond-to-max('lg') {
      :deep(.arco-input-wrapper) {
        height: 44px;
        .arco-input {
          font-size: 16px;
        }
      }
      :deep(.arco-input-search-btn) {
        height: 44px;
        min-width: 80px;
      }
    }
  }

  &__suggestions {
    position: absolute;
    top: 52px;
    left: 0;
    z-index: 10;
    width: 100%;
  }

  @include respond-to-max('xl') {
    padding: 32px 0 40px;

    &__title {
      font-size: $font-size-display-1;
      line-height: $line-height-display-1;
      margin-bottom: 16px;
    }
  }

  @include respond-to-max('lg') {
    padding: 32px 16px 24px;

    &__title {
      font-size: 28px;
      margin-bottom: 24px;
    }
  }

  @include respond-to-max('sm') {
    padding: 24px 16px 20px;

    &__title {
      font-size: 24px;
      margin-bottom: 20px;
    }
  }
}
</style>
